/// Soft prompt for signed-in players who still have a server auto-generated name.

import { create } from 'zustand';

import {
  isAutoUsernamePromptDismissed,
  setAutoUsernamePromptDismissed,
} from '@/user/data/userLocalData';
import { isAutoGeneratedUsername } from '@/user/domain/username';

interface AutoUsernamePromptState {
  visible: boolean;
  /** User ids that chose "Not now" this page load. */
  skippedThisSession: Set<string>;
  consider: (userId: string, username: string, isAnonymous: boolean) => void;
  changeNow: (userId: string) => void;
  dontAskAgain: (userId: string) => void;
  notNow: (userId: string) => void;
  hide: () => void;
}

export const useAutoUsernamePromptStore = create<AutoUsernamePromptState>((set, get) => ({
  visible: false,
  skippedThisSession: new Set(),

  consider: (userId, username, isAnonymous) => {
    if (isAnonymous || !userId) {
      set({ visible: false });
      return;
    }
    if (!isAutoGeneratedUsername(username)) {
      set({ visible: false });
      return;
    }
    if (isAutoUsernamePromptDismissed(userId)) {
      set({ visible: false });
      return;
    }
    if (get().skippedThisSession.has(userId)) {
      set({ visible: false });
      return;
    }
    set({ visible: true });
  },

  changeNow: (userId) => {
    const skipped = new Set(get().skippedThisSession);
    skipped.add(userId);
    set({ visible: false, skippedThisSession: skipped });
  },

  dontAskAgain: (userId) => {
    setAutoUsernamePromptDismissed(userId);
    const skipped = new Set(get().skippedThisSession);
    skipped.add(userId);
    set({ visible: false, skippedThisSession: skipped });
  },

  notNow: (userId) => {
    const skipped = new Set(get().skippedThisSession);
    skipped.add(userId);
    set({ visible: false, skippedThisSession: skipped });
  },

  hide: () => set({ visible: false }),
}));
